import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import { fetchQuery, graphql } from 'relay-runtime';
import environment from 'api/environment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import ProfileIcon from 'sharedComponents/core/ProfileIcon';
import { DelegatePickerQueryResponse } from './__generated__/DelegatePickerQuery.graphql';

// The delegate a background check will be requested for. `hasActiveBackgroundCheck`
// drives whether the "Initial" option is available (an existing check means the
// next one can only be a renewal).
export type DelegateOption = {
  uuid: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  profileImageUrl?: string | null;
  hasActiveBackgroundCheck: boolean;
};

const query = graphql`
  query DelegatePickerQuery($text: String) {
    delegates(filter: { name: $text }, page: { limit: 20 }) {
      edges {
        uuid
        firstName
        lastName
        email
        profileImageUrl
        hasActiveBackgroundCheck
      }
    }
  }
`;

const useStyles = createUseStyles((theme: Theme) => ({
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 14px',
    minHeight: 40,
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 8,
    marginBottom: 12,
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus-within': {
      borderColor: '#0b57ff',
      boxShadow: '0 0 0 3px rgba(11, 87, 255, 0.1)'
    }
  },
  searchIcon: {
    color: '#9ea2ad',
    fontSize: 14
  },
  searchInput: {
    flex: 1,
    marginLeft: 12,
    border: 'none',
    outline: 'none',
    fontSize: 15,
    color: theme.colors.primaryBlack,
    '&::placeholder': {
      color: '#9ea2ad'
    }
  },
  list: {
    maxHeight: 320,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gridGap: 4
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 14px',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': {
      background: '#f8f9fb'
    }
  },
  rowText: {
    minWidth: 0,
    marginLeft: 12
  },
  name: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1c1c1c'
  },
  email: {
    fontSize: 12.5,
    color: '#6A728F',
    marginTop: 2
  },
  message: {
    padding: '28px 14px',
    textAlign: 'center',
    color: '#6A728F',
    fontSize: 14
  }
}));

type Props = {
  onSelect: (delegate: DelegateOption) => void;
};

function DelegatePicker({ onSelect }: Props) {
  const classes = useStyles();
  const [text, setText] = React.useState('');
  const [results, setResults] = React.useState<DelegateOption[]>([]);
  const [loading, setLoading] = React.useState(false);
  const debouncer = React.useRef<number>();

  const run = (queryText: string) => {
    setLoading(true);
    clearTimeout(debouncer.current);
    debouncer.current = setTimeout(async () => {
      try {
        // Omit the filter on an empty query: an empty-string ILIKE drops rows
        // with NULL name columns, matching how the other delegate lists behave.
        const data = (await fetchQuery(environment, query, {
          text: queryText.length > 0 ? queryText : undefined
        })) as DelegatePickerQueryResponse;
        const edges = data?.delegates?.edges ?? [];
        setResults(
          edges
            .filter((edge): edge is NonNullable<typeof edge> => Boolean(edge))
            .map((edge) => ({
              uuid: edge.uuid,
              firstName: edge.firstName ?? '',
              lastName: edge.lastName ?? '',
              email: edge.email,
              profileImageUrl: edge.profileImageUrl,
              hasActiveBackgroundCheck: edge.hasActiveBackgroundCheck ?? false
            }))
        );
      } catch (err) {
        console.error('Delegate search failed', err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300) as unknown as number;
  };

  React.useEffect(() => {
    run('');
    return () => clearTimeout(debouncer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className={classes.searchRow}>
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className={classes.searchIcon}
        />
        <input
          className={classes.searchInput}
          placeholder="Search delegates by name"
          value={text}
          autoFocus
          onChange={(evt) => {
            setText(evt.target.value);
            run(evt.target.value);
          }}
        />
      </div>
      <div className={classes.list}>
        {results.length === 0 ? (
          <div className={classes.message}>
            {loading ? 'Searching…' : 'No delegates found'}
          </div>
        ) : (
          results.map((delegate) => (
            <div
              key={delegate.uuid}
              className={classes.row}
              onClick={() => onSelect(delegate)}
            >
              <ProfileIcon
                name={`${delegate.firstName} ${delegate.lastName}`}
                url={delegate.profileImageUrl ?? undefined}
                size={34}
              />
              <div className={classes.rowText}>
                <div className={classes.name}>
                  {delegate.firstName} {delegate.lastName}
                </div>
                {delegate.email && (
                  <div className={classes.email}>{delegate.email}</div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default DelegatePicker;
